import React, { useState, useEffect, useMemo } from 'react';
import {
  AlertTriangle, Activity, Clock, RefreshCcw, Sparkles, MessageSquare, Mic,
  Edit3, Mail, CheckCircle2, ShieldCheck, ShieldAlert, ShieldQuestion, Plus,
  ArrowLeft, ChevronRight, FileText, Loader2, BadgeCheck, XCircle
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../utils/api';

const COLOR_MAP = {
  rojo:     { bg: 'bg-red-100',     text: 'text-red-700',     border: 'border-red-300',     dot: 'bg-red-500',     label: 'CRÍTICO' },
  amarillo: { bg: 'bg-amber-100',   text: 'text-amber-700',   border: 'border-amber-300',   dot: 'bg-amber-500',   label: 'EN RIESGO' },
  verde:    { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-300', dot: 'bg-emerald-500', label: 'SALUDABLE' }
};

const Semaforo = ({ salud, size = 'md' }) => {
  const c = COLOR_MAP[salud?.color] || COLOR_MAP.verde;
  const dim = size === 'lg' ? 'w-5 h-5' : 'w-3 h-3';
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${c.bg} ${c.border}`}>
      <span className={`${dim} rounded-full ${c.dot} ring-2 ring-white`} />
      <span className={`text-xs font-semibold ${c.text}`}>{c.label}</span>
    </div>
  );
};

const Satisfaccion360 = () => {
  const [loading, setLoading] = useState(true);
  const [clientes, setClientes] = useState([]);
  const [prioridades, setPrioridades] = useState(null);
  const [filtro, setFiltro] = useState('todos'); // todos, rojo, amarillo, verde, criticos

  const [muroOpen, setMuroOpen] = useState(false);
  const [muroData, setMuroData] = useState(null);
  const [muroLoading, setMuroLoading] = useState(false);
  const [analizandoIA, setAnalizandoIA] = useState(false);

  // Modales
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ contenido: '', canal: 'manual', es_negativo: false });

  const [notaCierreOpen, setNotaCierreOpen] = useState(false);
  const [notaCierreCtx, setNotaCierreCtx] = useState(null); // feedback item
  const [notaForm, setNotaForm] = useState({ solucion: '', prevencion: '' });

  const [validacionOpen, setValidacionOpen] = useState(false);
  const [validacionCtx, setValidacionCtx] = useState(null);
  const [validacionForm, setValidacionForm] = useState({ cliente_satisfecho: true, observaciones: '' });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [resCli, resPri] = await Promise.all([
        api.get('/post-venta/satisfaccion/clientes'),
        api.get('/post-venta/satisfaccion/prioridades')
      ]);
      setClientes(resCli.data.clientes || []);
      setPrioridades(resPri.data);
    } catch (e) {
      toast.error('Error cargando datos de satisfacción');
    } finally {
      setLoading(false);
    }
  };

  const abrirMuro = async (clienteId) => {
    setMuroOpen(true);
    setMuroLoading(true);
    try {
      const res = await api.get(`/post-venta/satisfaccion/clientes/${clienteId}/muro`);
      setMuroData(res.data);
    } catch (e) {
      toast.error('Error cargando muro');
      setMuroOpen(false);
    } finally {
      setMuroLoading(false);
    }
  };

  const cerrarMuro = () => {
    setMuroOpen(false);
    setMuroData(null);
  };

  const handleCrearFeedback = async () => {
    if (!feedbackForm.contenido.trim() || feedbackForm.contenido.trim().length < 3) {
      toast.error('El contenido debe tener al menos 3 caracteres');
      return;
    }
    try {
      const res = await api.post('/post-venta/satisfaccion/feedback', {
        cliente_id: muroData.cliente.id,
        contenido: feedbackForm.contenido.trim(),
        canal: feedbackForm.canal,
        es_negativo: feedbackForm.es_negativo
      });
      if (res.data.es_critico) {
        toast.success('🚨 ALERTA CRÍTICA registrada. Socios notificados.');
      } else {
        toast.success(res.data.message);
      }
      setFeedbackOpen(false);
      setFeedbackForm({ contenido: '', canal: 'manual', es_negativo: false });
      // Refrescar muro
      await abrirMuro(muroData.cliente.id);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const handleAnalizarIA = async () => {
    if (!muroData?.cliente?.id) return;
    setAnalizandoIA(true);
    try {
      const res = await api.post(`/post-venta/satisfaccion/clientes/${muroData.cliente.id}/analizar-minutas`);
      const n = res.data.items_creados || 0;
      if (n > 0) toast.success(`IA detectó ${n} ítem(s) negativo(s) en minutas`);
      else toast.info('IA no detectó sentimiento negativo en las minutas recientes');
      await abrirMuro(muroData.cliente.id);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en análisis IA');
    } finally {
      setAnalizandoIA(false);
    }
  };

  const abrirNotaCierre = (item) => {
    setNotaCierreCtx(item);
    setNotaForm({ solucion: '', prevencion: '' });
    setNotaCierreOpen(true);
  };

  const handleGuardarNotaCierre = async () => {
    if (notaForm.solucion.trim().length < 10) {
      toast.error('La solución debe tener al menos 10 caracteres');
      return;
    }
    if (notaForm.prevencion.trim().length < 5) {
      toast.error('La acción de prevención debe tener al menos 5 caracteres');
      return;
    }
    try {
      await api.post(`/post-venta/satisfaccion/feedback/${notaCierreCtx.id}/nota-cierre`, notaForm);
      toast.success('Nota de Cierre registrada. Pendiente de validación comercial.');
      setNotaCierreOpen(false);
      await abrirMuro(muroData.cliente.id);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const abrirValidacion = (item) => {
    setValidacionCtx(item);
    setValidacionForm({ cliente_satisfecho: true, observaciones: '' });
    setValidacionOpen(true);
  };

  const handleValidar = async () => {
    try {
      await api.post(`/post-venta/satisfaccion/feedback/${validacionCtx.id}/validar`, validacionForm);
      toast.success(validacionForm.cliente_satisfecho ? 'Caso validado y cerrado ✓' : 'Caso reabierto');
      setValidacionOpen(false);
      await abrirMuro(muroData.cliente.id);
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const clientesFiltrados = useMemo(() => {
    if (filtro === 'todos') return clientes;
    if (filtro === 'criticos') return clientes.filter(c => c.es_critico);
    return clientes.filter(c => c.salud?.color === filtro);
  }, [clientes, filtro]);

  const kpis = useMemo(() => {
    const total = clientes.length;
    const rojos = clientes.filter(c => c.salud?.color === 'rojo').length;
    const amarillos = clientes.filter(c => c.salud?.color === 'amarillo').length;
    const verdes = clientes.filter(c => c.salud?.color === 'verde').length;
    const criticos = clientes.filter(c => c.es_critico).length;
    return { total, rojos, amarillos, verdes, criticos };
  }, [clientes]);

  if (loading) {
    return (
      <Card className="p-12 text-center" data-testid="satisfaccion-loading">
        <Loader2 className="w-8 h-8 text-rose-400 animate-spin mx-auto mb-3" />
        <p className="text-slate-600">Cargando Satisfacción 360°...</p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="satisfaccion-360-root">
      {/* KPIs Cabecera */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="satisfaccion-kpis">
        <Card className="p-4 border-slate-200">
          <p className="text-xs uppercase text-slate-500 font-semibold">Clientes activos</p>
          <p className="text-3xl font-bold text-slate-800">{kpis.total}</p>
        </Card>
        <Card className="p-4 border-red-200 bg-red-50">
          <p className="text-xs uppercase text-red-700 font-semibold flex items-center gap-1">
            <ShieldAlert className="w-3 h-3" /> Críticos
          </p>
          <p className="text-3xl font-bold text-red-700">{kpis.rojos}</p>
        </Card>
        <Card className="p-4 border-amber-200 bg-amber-50">
          <p className="text-xs uppercase text-amber-700 font-semibold flex items-center gap-1">
            <ShieldQuestion className="w-3 h-3" /> En riesgo
          </p>
          <p className="text-3xl font-bold text-amber-700">{kpis.amarillos}</p>
        </Card>
        <Card className="p-4 border-emerald-200 bg-emerald-50">
          <p className="text-xs uppercase text-emerald-700 font-semibold flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Saludables
          </p>
          <p className="text-3xl font-bold text-emerald-700">{kpis.verdes}</p>
        </Card>
        <Card className="p-4 border-purple-200 bg-purple-50">
          <p className="text-xs uppercase text-purple-700 font-semibold flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> &gt; 100 UF
          </p>
          <p className="text-3xl font-bold text-purple-700">{kpis.criticos}</p>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Smart Targeting */}
        <div className="lg:col-span-1 space-y-4" data-testid="smart-targeting-panel">
          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-rose-500" /> Smart Targeting
          </h3>

          {/* 1. Feedback negativo activo */}
          <Card className="border-red-200" data-testid="targeting-feedback-activo">
            <div className="p-3 border-b border-red-200 bg-red-50 rounded-t-lg flex items-center gap-2">
              <Activity className="w-4 h-4 text-red-600" />
              <h4 className="font-semibold text-sm text-red-800">Feedback negativo activo</h4>
              <Badge className="bg-red-200 text-red-800 ml-auto">{prioridades?.feedback_activo?.length || 0}</Badge>
            </div>
            <div className="max-h-[180px] overflow-y-auto">
              {(prioridades?.feedback_activo || []).length === 0 ? (
                <p className="p-3 text-xs text-slate-500 italic">Sin casos abiertos.</p>
              ) : (
                prioridades.feedback_activo.map(f => (
                  <button
                    key={f.feedback_id}
                    onClick={() => abrirMuro(f.cliente_id)}
                    className="w-full text-left p-3 border-b border-slate-100 hover:bg-red-50 transition-colors"
                    data-testid={`tgt-feedback-${f.feedback_id}`}
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-800 truncate flex-1">{f.cliente_nombre}</p>
                      {f.es_critico && <Badge className="bg-red-600 text-white text-[10px]">CRÍTICO</Badge>}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{f.contenido}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">{f.monto_uf} UF · {f.estado}</p>
                  </button>
                ))
              )}
            </div>
          </Card>

          {/* 2. Silencio operativo */}
          <Card className="border-amber-200" data-testid="targeting-silencio">
            <div className="p-3 border-b border-amber-200 bg-amber-50 rounded-t-lg flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <h4 className="font-semibold text-sm text-amber-800">Silencio operativo (&gt;45d)</h4>
              <Badge className="bg-amber-200 text-amber-800 ml-auto">{prioridades?.silencio_operativo?.length || 0}</Badge>
            </div>
            <div className="max-h-[180px] overflow-y-auto">
              {(prioridades?.silencio_operativo || []).length === 0 ? (
                <p className="p-3 text-xs text-slate-500 italic">Todos los clientes con contacto reciente.</p>
              ) : (
                prioridades.silencio_operativo.map(s => (
                  <button
                    key={s.cliente_id}
                    onClick={() => abrirMuro(s.cliente_id)}
                    className="w-full text-left p-3 border-b border-slate-100 hover:bg-amber-50 transition-colors"
                    data-testid={`tgt-silencio-${s.cliente_id}`}
                  >
                    <p className="text-sm font-medium text-slate-800 truncate">{s.cliente_nombre}</p>
                    <p className="text-xs text-slate-500">{s.dias_sin_contacto} días sin contacto · {s.monto_uf} UF</p>
                  </button>
                ))
              )}
            </div>
          </Card>

          {/* 3. Renovación crítica */}
          <Card className="border-purple-200" data-testid="targeting-renovacion">
            <div className="p-3 border-b border-purple-200 bg-purple-50 rounded-t-lg flex items-center gap-2">
              <RefreshCcw className="w-4 h-4 text-purple-600" />
              <h4 className="font-semibold text-sm text-purple-800">Renovación crítica (mes 3 CPS)</h4>
              <Badge className="bg-purple-200 text-purple-800 ml-auto">{prioridades?.renovacion_critica?.length || 0}</Badge>
            </div>
            <div className="max-h-[180px] overflow-y-auto">
              {(prioridades?.renovacion_critica || []).length === 0 ? (
                <p className="p-3 text-xs text-slate-500 italic">Ninguna ventana de renovación en riesgo.</p>
              ) : (
                prioridades.renovacion_critica.map(r => (
                  <button
                    key={`${r.cliente_id}-${r.psp_folio}`}
                    onClick={() => abrirMuro(r.cliente_id)}
                    className="w-full text-left p-3 border-b border-slate-100 hover:bg-purple-50 transition-colors"
                    data-testid={`tgt-renovacion-${r.cliente_id}`}
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-slate-800 truncate flex-1">{r.cliente_nombre}</p>
                      <Semaforo salud={r.salud} />
                    </div>
                    <p className="text-xs text-slate-500">{r.psp_folio} · día {r.dias_activa} · {r.monto_uf} UF</p>
                  </button>
                ))
              )}
            </div>
          </Card>
        </div>

        {/* Listado clientes con semáforo */}
        <div className="lg:col-span-2 space-y-3" data-testid="clientes-listado">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Activity className="w-5 h-5 text-rose-500" /> Clientes activos · Semáforo de salud
            </h3>
            <div className="flex items-center gap-2">
              <Select value={filtro} onValueChange={setFiltro}>
                <SelectTrigger className="h-9 w-[180px]" data-testid="filter-salud-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="rojo">Solo Críticos</SelectItem>
                  <SelectItem value="amarillo">Solo En Riesgo</SelectItem>
                  <SelectItem value="verde">Solo Saludables</SelectItem>
                  <SelectItem value="criticos">Solo &gt; 100 UF</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={fetchAll} data-testid="reload-clientes-btn">
                <RefreshCcw className="w-3.5 h-3.5 mr-1" /> Refrescar
              </Button>
            </div>
          </div>

          {clientesFiltrados.length === 0 ? (
            <Card className="p-10 text-center border-slate-200">
              <p className="text-slate-500">No hay clientes que coincidan con el filtro.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {clientesFiltrados.map(c => {
                const cm = COLOR_MAP[c.salud?.color] || COLOR_MAP.verde;
                return (
                  <Card
                    key={c.id}
                    className={`p-4 border ${cm.border} hover:shadow-md transition-all cursor-pointer`}
                    onClick={() => abrirMuro(c.id)}
                    data-testid={`cliente-card-${c.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-3 h-3 rounded-full ${cm.dot} ring-4 ring-white shadow`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                        <p className="text-xs text-slate-500 truncate">
                          {c.company || c.rut || '—'} · {c.salud?.razon}
                          {c.salud?.nps_promedio != null && ` · NPS ${c.salud.nps_promedio}/5`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-slate-700">{c.monto_total_uf} UF</p>
                        {c.es_critico && (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px]">
                            <AlertTriangle className="w-3 h-3 mr-0.5" /> Alto valor
                          </Badge>
                        )}
                      </div>
                      <Semaforo salud={c.salud} />
                      <ChevronRight className="w-5 h-5 text-slate-300" />
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ============= MURO DE FEEDBACK ============= */}
      <Dialog open={muroOpen} onOpenChange={(v) => !v && cerrarMuro()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="muro-dialog">
          {muroLoading || !muroData ? (
            <div className="py-12 text-center">
              <Loader2 className="w-8 h-8 text-rose-400 animate-spin mx-auto mb-3" />
              <p className="text-slate-500">Cargando muro...</p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between pr-8">
                  <div>
                    <DialogTitle className="text-2xl flex items-center gap-2">
                      <MessageSquare className="w-6 h-6 text-rose-500" />
                      {muroData.cliente.name}
                    </DialogTitle>
                    <DialogDescription>
                      {muroData.cliente.company || muroData.cliente.rut || '—'} · {muroData.cliente.email || 'sin email'}
                    </DialogDescription>
                  </div>
                  <Semaforo salud={muroData.salud} size="lg" />
                </div>
              </DialogHeader>

              {/* Resumen */}
              <div className="grid grid-cols-3 gap-3 my-3">
                <Card className="p-3 border-slate-200">
                  <p className="text-xs uppercase text-slate-500 font-semibold">Monto activo (PSPs)</p>
                  <p className="text-xl font-bold text-slate-800">{muroData.monto_total_uf} UF</p>
                  {muroData.monto_total_uf > muroData.umbral_critico_uf && (
                    <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px] mt-1">
                      Cliente de alto valor (&gt;{muroData.umbral_critico_uf} UF)
                    </Badge>
                  )}
                </Card>
                <Card className="p-3 border-slate-200">
                  <p className="text-xs uppercase text-slate-500 font-semibold">NPS Promedio</p>
                  <p className="text-xl font-bold text-slate-800">
                    {muroData.salud.nps_promedio != null ? `${muroData.salud.nps_promedio}/5` : '—'}
                  </p>
                  <p className="text-[11px] text-slate-500">{muroData.salud.razon}</p>
                </Card>
                <Card className="p-3 border-slate-200">
                  <p className="text-xs uppercase text-slate-500 font-semibold">Feedback negativo activo</p>
                  <p className="text-xl font-bold text-slate-800">{muroData.salud.feedback_negativo_activo}</p>
                </Card>
              </div>

              {/* Acciones */}
              <div className="flex flex-wrap gap-2 mb-3">
                <Button
                  size="sm"
                  className="bg-rose-500 hover:bg-rose-600"
                  onClick={() => setFeedbackOpen(true)}
                  data-testid="add-feedback-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Nuevo feedback
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                  onClick={handleAnalizarIA}
                  disabled={analizandoIA}
                  data-testid="analyze-ia-btn"
                >
                  {analizandoIA ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Analizando...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-1" /> Analizar minutas con IA</>
                  )}
                </Button>
              </div>

              {/* Timeline */}
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-rose-500" /> Muro de Feedback ({muroData.items.length})
              </h4>
              {muroData.items.length === 0 ? (
                <Card className="p-8 text-center border-dashed">
                  <p className="text-slate-500 text-sm">Sin actividad registrada todavía.</p>
                </Card>
              ) : (
                <div className="space-y-3" data-testid="muro-timeline">
                  {muroData.items.map(item => (
                    <TimelineItem
                      key={`${item.tipo}-${item.id}`}
                      item={item}
                      onNotaCierre={() => abrirNotaCierre(item)}
                      onValidar={() => abrirValidacion(item)}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ============= MODAL NUEVO FEEDBACK ============= */}
      <Dialog open={feedbackOpen} onOpenChange={setFeedbackOpen}>
        <DialogContent className="max-w-lg" data-testid="feedback-form-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo feedback manual</DialogTitle>
            <DialogDescription>
              Registra una nota interna del Líder Comercial. Si lo marcas como negativo, se generará una tarea automática de resolución.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Canal</Label>
              <Select value={feedbackForm.canal} onValueChange={(v) => setFeedbackForm({ ...feedbackForm, canal: v })}>
                <SelectTrigger data-testid="feedback-canal-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Nota interna</SelectItem>
                  <SelectItem value="llamada">Llamada</SelectItem>
                  <SelectItem value="reunion">Reunión</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Contenido *</Label>
              <Textarea
                rows={5}
                placeholder="Describe el feedback o situación del cliente..."
                value={feedbackForm.contenido}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, contenido: e.target.value })}
                data-testid="feedback-contenido-input"
              />
            </div>
            <label className="flex items-start gap-2 p-3 border border-red-200 bg-red-50 rounded-lg cursor-pointer">
              <input
                type="checkbox"
                checked={feedbackForm.es_negativo}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, es_negativo: e.target.checked })}
                className="mt-0.5"
                data-testid="feedback-negativo-check"
              />
              <div>
                <p className="text-sm font-medium text-red-800">Es feedback negativo / problema</p>
                <p className="text-xs text-red-600">
                  Se creará una tarea de resolución y, si el cliente supera 100 UF activas, una ALERTA CRÍTICA con notificación a Socios.
                </p>
              </div>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFeedbackOpen(false)}>Cancelar</Button>
            <Button
              className="bg-rose-500 hover:bg-rose-600"
              onClick={handleCrearFeedback}
              data-testid="feedback-submit-btn"
            >
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= MODAL NOTA DE CIERRE ============= */}
      <Dialog open={notaCierreOpen} onOpenChange={setNotaCierreOpen}>
        <DialogContent className="max-w-lg" data-testid="nota-cierre-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Nota de Cierre obligatoria
            </DialogTitle>
            <DialogDescription>
              Documenta cómo se resolvió el caso. Esta nota será enviada al Líder Comercial para validación final.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Solución aplicada *</Label>
              <Textarea
                rows={4}
                placeholder="Describe la acción concreta tomada para resolver el caso (mín. 10 caracteres)"
                value={notaForm.solucion}
                onChange={(e) => setNotaForm({ ...notaForm, solucion: e.target.value })}
                data-testid="nota-solucion-input"
              />
            </div>
            <div>
              <Label>Acciones para evitar repetición *</Label>
              <Textarea
                rows={3}
                placeholder="Qué se hará para que esto no vuelva a ocurrir (mín. 5 caracteres)"
                value={notaForm.prevencion}
                onChange={(e) => setNotaForm({ ...notaForm, prevencion: e.target.value })}
                data-testid="nota-prevencion-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotaCierreOpen(false)}>Cancelar</Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleGuardarNotaCierre}
              data-testid="nota-submit-btn"
            >
              Guardar y enviar a validación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============= MODAL VALIDACIÓN COMERCIAL ============= */}
      <Dialog open={validacionOpen} onOpenChange={setValidacionOpen}>
        <DialogContent className="max-w-lg" data-testid="validacion-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-blue-600" /> Validación del Líder Comercial
            </DialogTitle>
            <DialogDescription>
              Confirma si el cliente quedó realmente satisfecho con la solución. Si rechazas, el caso se reabrirá.
            </DialogDescription>
          </DialogHeader>
          {validacionCtx?.nota_cierre && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-2">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase">Solución</p>
                <p className="text-slate-700 whitespace-pre-line">{validacionCtx.nota_cierre.solucion}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase">Prevención</p>
                <p className="text-slate-700 whitespace-pre-line">{validacionCtx.nota_cierre.prevencion}</p>
              </div>
              <p className="text-[11px] text-slate-400">
                Por {validacionCtx.nota_cierre.creada_por} · {new Date(validacionCtx.nota_cierre.fecha).toLocaleDateString('es-CL')}
              </p>
            </div>
          )}
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setValidacionForm({ ...validacionForm, cliente_satisfecho: true })}
                className={`p-3 rounded-lg border-2 text-center transition-all ${validacionForm.cliente_satisfecho ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}
                data-testid="validar-aprobar"
              >
                <CheckCircle2 className={`w-6 h-6 mx-auto mb-1 ${validacionForm.cliente_satisfecho ? 'text-emerald-600' : 'text-slate-400'}`} />
                <p className="text-sm font-semibold">Cliente satisfecho</p>
                <p className="text-[11px] text-slate-500">Cerrar y restaurar salud</p>
              </button>
              <button
                type="button"
                onClick={() => setValidacionForm({ ...validacionForm, cliente_satisfecho: false })}
                className={`p-3 rounded-lg border-2 text-center transition-all ${!validacionForm.cliente_satisfecho ? 'border-red-500 bg-red-50' : 'border-slate-200'}`}
                data-testid="validar-rechazar"
              >
                <XCircle className={`w-6 h-6 mx-auto mb-1 ${!validacionForm.cliente_satisfecho ? 'text-red-600' : 'text-slate-400'}`} />
                <p className="text-sm font-semibold">Reabrir caso</p>
                <p className="text-[11px] text-slate-500">No quedó conforme</p>
              </button>
            </div>
            <div>
              <Label>Observaciones {!validacionForm.cliente_satisfecho && '*'}</Label>
              <Textarea
                rows={3}
                placeholder={validacionForm.cliente_satisfecho ? 'Opcional' : 'Indica por qué se reabre y qué falta'}
                value={validacionForm.observaciones}
                onChange={(e) => setValidacionForm({ ...validacionForm, observaciones: e.target.value })}
                data-testid="validar-observaciones-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidacionOpen(false)}>Cancelar</Button>
            <Button
              className={validacionForm.cliente_satisfecho ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              onClick={handleValidar}
              data-testid="validar-submit-btn"
            >
              Confirmar validación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============= TIMELINE ITEM =============
const TimelineItem = ({ item, onNotaCierre, onValidar }) => {
  const ICONS = {
    encuesta: <Mail className="w-4 h-4" />,
    feedback: item.icono === 'mic' ? <Mic className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />
  };
  const isNeg = item.es_negativo;
  const estado = item.estado;
  const tieneCierre = !!item.nota_cierre;
  const tieneValidacion = !!item.validacion_comercial;
  const validada = tieneValidacion && item.validacion_comercial?.cliente_satisfecho;

  // Color barra lateral
  let sideColor = 'bg-slate-300';
  if (isNeg && estado === 'abierto') sideColor = 'bg-red-500';
  else if (isNeg && estado === 'en_resolucion') sideColor = 'bg-amber-500';
  else if (isNeg && estado === 'pendiente_validacion') sideColor = 'bg-blue-500';
  else if (isNeg && estado === 'cerrado_validado') sideColor = 'bg-emerald-500';
  else if (item.tipo === 'encuesta') sideColor = 'bg-rose-400';
  else if (item.tipo === 'feedback' && !isNeg) sideColor = 'bg-slate-400';

  return (
    <div className="flex gap-3" data-testid={`timeline-item-${item.id}`}>
      <div className="flex flex-col items-center pt-1">
        <div className={`w-8 h-8 rounded-full ${sideColor} text-white flex items-center justify-center`}>
          {ICONS[item.tipo] || <FileText className="w-4 h-4" />}
        </div>
        <div className="flex-1 w-px bg-slate-200" />
      </div>
      <Card className={`flex-1 p-3 border ${isNeg ? 'border-red-200' : 'border-slate-200'}`}>
        <div className="flex items-start justify-between gap-2 mb-1 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-slate-800 text-sm">{item.titulo}</p>
            {item.es_critico && <Badge className="bg-red-600 text-white text-[10px]">🚨 CRÍTICO</Badge>}
            {item.tipo === 'encuesta' && item.nota != null && (
              <Badge className={`text-[10px] ${item.nota >= 4 ? 'bg-emerald-100 text-emerald-700' : item.nota >= 3 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                {item.nota}/5
              </Badge>
            )}
            {isNeg && estado && (
              <Badge className={`text-[10px] ${
                estado === 'abierto' ? 'bg-red-100 text-red-700' :
                estado === 'en_resolucion' ? 'bg-amber-100 text-amber-700' :
                estado === 'pendiente_validacion' ? 'bg-blue-100 text-blue-700' :
                estado === 'cerrado_validado' ? 'bg-emerald-100 text-emerald-700' :
                'bg-slate-100 text-slate-700'
              }`}>
                {estado.replace(/_/g, ' ')}
              </Badge>
            )}
            {validada && <Badge className="bg-emerald-600 text-white text-[10px]"><BadgeCheck className="w-3 h-3 mr-0.5" /> Validado</Badge>}
          </div>
          <p className="text-[11px] text-slate-400 whitespace-nowrap">
            {item.fecha ? new Date(item.fecha).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : ''}
          </p>
        </div>
        <p className="text-sm text-slate-700 whitespace-pre-line">{item.contenido}</p>
        {item.created_by_name && (
          <p className="text-[11px] text-slate-400 mt-1">Por: {item.created_by_name}</p>
        )}

        {/* Nota de Cierre embebida */}
        {item.nota_cierre && (
          <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg space-y-1">
            <p className="text-[11px] font-semibold uppercase text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Nota de Cierre
            </p>
            <p className="text-sm text-slate-700"><strong>Solución:</strong> {item.nota_cierre.solucion}</p>
            <p className="text-sm text-slate-700"><strong>Prevención:</strong> {item.nota_cierre.prevencion}</p>
            <p className="text-[11px] text-slate-500">Por {item.nota_cierre.creada_por} · {new Date(item.nota_cierre.fecha).toLocaleDateString('es-CL')}</p>
          </div>
        )}

        {/* Validación comercial embebida */}
        {item.validacion_comercial && (
          <div className={`mt-2 p-3 rounded-lg border ${validada ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
            <p className={`text-[11px] font-semibold uppercase flex items-center gap-1 ${validada ? 'text-blue-700' : 'text-red-700'}`}>
              {validada ? <BadgeCheck className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
              {validada ? 'Validado por Líder Comercial' : 'Caso reabierto por Líder Comercial'}
            </p>
            {item.validacion_comercial.observaciones && (
              <p className="text-sm text-slate-700 mt-1">{item.validacion_comercial.observaciones}</p>
            )}
            <p className="text-[11px] text-slate-500">Por {item.validacion_comercial.validada_por} · {new Date(item.validacion_comercial.fecha).toLocaleDateString('es-CL')}</p>
          </div>
        )}

        {/* Acciones contextuales */}
        {item.tipo === 'feedback' && isNeg && (
          <div className="mt-2 flex gap-2 flex-wrap">
            {!tieneCierre && (estado === 'abierto' || estado === 'en_resolucion') && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={onNotaCierre} data-testid={`add-nota-cierre-${item.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Agregar Nota de Cierre
              </Button>
            )}
            {tieneCierre && !tieneValidacion && estado === 'pendiente_validacion' && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 h-7 text-xs" onClick={onValidar} data-testid={`validar-${item.id}`}>
                <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Validar caso
              </Button>
            )}
            {estado === 'reabierto' && (
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" onClick={onNotaCierre} data-testid={`add-nota-cierre-${item.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Nueva Nota de Cierre
              </Button>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default Satisfaccion360;
